// Lead Generation System — staff earnings review + weekly pay-period finalize +
// finalized pay-period ledger API client (`api/lead-gen/earnings`). DISPLAY-ONLY:
// nothing here moves real money. Grade + bonus multiplier are AUTOMATIC; "Lock"
// freezes the figures a manager reads and then pays off-system. Reads gate on
// LeadGen.View; review/finalize actions on LeadGen.ReviewEarnings.

import { api } from "@/api/client";

export type LeadGenEarningState = "Estimated" | "PendingReview" | "Approved" | "Adjusted";

export interface LeadGenEarningResultDto {
  id: string;
  leadGeneratorId: string;
  preleadId?: string | null;
  leadId?: string | null;
  kpiCode: string;
  bucket: string;
  earnedAmount: number;
  creditedAmount: number;
  capReason?: string | null;
  state: LeadGenEarningState;
  isAdjustment: boolean;
  sourceKey: string;
  weekOf: string;
  recognizedAt: string;
  weeklyBucketCreditedTotal: number;
  weeklyCreditedTotal: number;
  alreadyRecognized: boolean;
}

export interface LeadGenBucketSummaryDto {
  bucket: string;
  count: number;
  earned: number;
  credited: number;
  capped: number;
  weeklyCap?: number | null;
  weeklyCapRemaining?: number | null;
  monthlyCap?: number | null;
  monthlyCapRemaining?: number | null;
}

export interface LeadGenEarningsSummaryDto {
  leadGeneratorId: string;
  weekOf: string;
  buckets: LeadGenBucketSummaryDto[];
  totalCount: number;
  totalEarned: number;
  totalCredited: number;
  totalCapped: number;
  weeklyTotalCap?: number | null;
  weeklyTotalCapRemaining?: number | null;
  monthlyTotalCap?: number | null;
  monthlyTotalCapRemaining?: number | null;
  estimatedCredited: number;
  pendingReviewCredited: number;
  approvedCredited: number;
}

export interface LeadGenPayPeriodDto {
  id: string;
  leadGeneratorId: string;
  weekOf: string;
  isLocked: boolean;
  lockedByUserId?: string | null;
  lockedAt?: string | null;
  unlockedByUserId?: string | null;
  unlockedAt?: string | null;
  qualityGrade?: string | null;
  qualityMultiplier?: number | null;
  gradeOverriddenByUserId?: string | null;
  approvedGross?: number | null;
  approvedNet?: number | null;
  notes?: string | null;
}

/** The AUTHORITATIVE automatic grade + bonus multiplier for a generator's pay
 *  week. The multiplier is upside-only (>= 1.00). Mirrors `LeadGenGradeResult`. */
export interface LeadGenGradeResultDto {
  weekOf: string;
  gradePercent: number;
  letter: string;
  band: string;
  multiplier: number;
  weeklyCap: number;
  approvedGross: number;
  totalAdjustments: number;
  approvedNet: number;
  nextLetter?: string | null;
  nextMultiplier: number;
  distanceToNext: number;
}

/** Manager's signed-dollar adjustment on the generator's current OPEN week.
 *  `weekOf` is advisory — the server posts to the current open week. */
export interface LeadGenManualAdjustRequest {
  leadGeneratorId: string;
  weekOf?: string | null;
  amount: number;
  reason: string;
}

// --- Finalized pay-period ledger --------------------------------------------

export interface LeadGenLedgerHistoryRowDto {
  payPeriodId: string;
  weekOf: string;
  approvedGross: number;
  approvedNet: number;
  qualityGrade?: string | null;
  qualityMultiplier?: number | null;
  totalAdjustments: number;
  lockedAt?: string | null;
}

export interface LeadGenLedgerWeekRowDto {
  leadGeneratorId: string;
  payPeriodId: string;
  weekOf: string;
  leadGeneratorName: string;
  approvedGross: number;
  approvedNet: number;
  qualityGrade?: string | null;
  qualityMultiplier?: number | null;
  totalAdjustments: number;
  lockedAt?: string | null;
}

export interface LeadGenLedgerLineItemDto {
  id: string;
  kpiCode: string;
  bucket: string;
  earnedAmount: number;
  creditedAmount: number;
  capReason?: string | null;
  state: LeadGenEarningState;
  isAdjustment: boolean;
  adjustmentReason?: string | null;
  approvedAtUtc?: string | null;
  recognizedAt: string;
  preleadId?: string | null;
  leadId?: string | null;
}

/** Build a `?a=b&c=d` suffix for the POST endpoints that take their scope from
 *  the query string (the `api` POST helper carries only a JSON body). */
function periodQuery(leadGeneratorId: string, weekOf: string): string {
  const params = new URLSearchParams({ leadGeneratorId, weekOf });
  return `?${params.toString()}`;
}

export const earningsApi = {
  summary: (leadGeneratorId: string, weekOf: string) =>
    api.get<LeadGenEarningsSummaryDto>("/lead-gen/earnings/summary", {
      leadGeneratorId,
      weekOf,
    }),
  payPeriods: (leadGeneratorId: string) =>
    api.get<LeadGenPayPeriodDto[]>("/lead-gen/earnings/pay-periods", {
      leadGeneratorId,
    }),
  grade: (leadGeneratorId: string, weekOf: string) =>
    api.get<LeadGenGradeResultDto>("/lead-gen/earnings/grade", {
      leadGeneratorId,
      weekOf,
    }),
  adjust: (body: LeadGenManualAdjustRequest) =>
    api.post<LeadGenEarningResultDto>("/lead-gen/earnings/adjust", body),
  approvePayPeriod: (leadGeneratorId: string, weekOf: string) =>
    api.post<LeadGenPayPeriodDto>(
      `/lead-gen/earnings/pay-periods/approve${periodQuery(leadGeneratorId, weekOf)}`,
    ),
  lockPayPeriod: (leadGeneratorId: string, weekOf: string) =>
    api.post<LeadGenPayPeriodDto>(
      `/lead-gen/earnings/pay-periods/lock${periodQuery(leadGeneratorId, weekOf)}`,
    ),
  unlockPayPeriod: (leadGeneratorId: string, weekOf: string) =>
    api.post<LeadGenPayPeriodDto>(
      `/lead-gen/earnings/pay-periods/unlock${periodQuery(leadGeneratorId, weekOf)}`,
    ),

  // Finalized pay-period ledger.
  ledgerHistory: (leadGeneratorId: string) =>
    api.get<LeadGenLedgerHistoryRowDto[]>("/lead-gen/earnings/ledger/history", {
      leadGeneratorId,
    }),
  ledgerWeek: (weekOf: string) =>
    api.get<LeadGenLedgerWeekRowDto[]>("/lead-gen/earnings/ledger/week", { weekOf }),
  ledgerWeekLineItems: (leadGeneratorId: string, weekOf: string) =>
    api.get<LeadGenLedgerLineItemDto[]>("/lead-gen/earnings/ledger/week/line-items", {
      leadGeneratorId,
      weekOf,
    }),
  // CSV exports stream `text/csv`, not the ApiResponse JSON envelope — fetched as
  // a Blob and saved via `downloadBlob`.
  exportWeekCsv: (weekOf: string) =>
    api.getBlob("/lead-gen/earnings/ledger/week/export", { weekOf }),
  exportHistoryCsv: (leadGeneratorId: string) =>
    api.getBlob("/lead-gen/earnings/ledger/history/export", { leadGeneratorId }),
};
